import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Plus } from "lucide-react";
import { MaskedLines, FadeUp, Eyebrow, EASE } from "@/components/Reveal";
import DemoForm from "@/components/DemoForm";
import JsonLd from "@/components/JsonLd";

const CONTACTS = [
  { icon: Phone, label: "Phone", value: "1300 277 689", note: "Sales & support" },
];

const FAQS = [
  {
    q: "Will it fit my excavator?",
    a: (<>The <Link to="/products/2.5d" data-testid="faq2-emg100-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">EMG100</Link> supports swing boom, triple boom, tilt bucket and offset boom machines. The <Link to="/products/3d" data-testid="faq2-tx73-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">TX73</Link> adds automated tilt control for Engcon and Steelwrist tilt-rotators. If it digs, we can guide it.</>),
  },
  {
    q: "How long does installation take?",
    a: "Mounting takes under two hours and calibration about thirty minutes per bucket. Most crews run their first production shift the same day.",
  },
  {
    q: "Do I need 3D design files to start?",
    a: (<>No. The <Link to="/products/2.5d" data-testid="faq-emg100-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">EMG100</Link> works from field-defined references — set a benchmark and dig. The <Link to="/products/3d" data-testid="faq-tx73-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">TX73</Link> adds model-driven 3D when your projects supply DWG, DXF or LandXML files.</>),
  },
  {
    q: "What happens after I buy?",
    a: "Onboarding on your site, over-the-air updates through the iSite cloud, and local technical support for as long as you run the system.",
  },
];

const FAQ_SCHEMA = {
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Will it fit my excavator?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The EMG100 supports swing boom, triple boom, tilt bucket and offset boom machines. The TX73 adds automated tilt control for Engcon and Steelwrist tilt-rotators. If it digs, we can guide it.",
      },
    },
    {
      "@type": "Question",
      name: "How long does installation take?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Mounting takes under two hours and calibration about thirty minutes per bucket. Most crews run their first production shift the same day.",
      },
    },
    {
      "@type": "Question",
      name: "Do I need 3D design files to start?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. The EMG100 works from field-defined references — set a benchmark and dig. The TX73 adds model-driven 3D when your projects supply DWG, DXF or LandXML files.",
      },
    },
    {
      "@type": "Question",
      name: "What happens after I buy?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Onboarding on your site, over-the-air updates through the iSite cloud, and local technical support for as long as you run the system.",
      },
    },
  ],
};

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-line">
      <button
        data-testid={`faq-toggle-${idx}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-display text-xl font-bold uppercase tracking-tight text-white sm:text-2xl">{q}</span>
        <Plus className={`h-5 w-5 shrink-0 text-signal transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
            data-testid={`faq-content-${idx}`}
          >
            <p className="max-w-3xl pb-6 leading-relaxed text-fog">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Contact() {
  return (
    <main>
      <JsonLd schema={FAQ_SCHEMA} testid="faq-schema" />
      <section className="relative overflow-hidden border-b border-line px-4 pb-16 pt-32 sm:px-8 lg:px-16 lg:pb-24 lg:pt-40" data-testid="contact-hero">
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto max-w-[1600px]">
          <FadeUp y={12}>
            <Eyebrow>Request a demo / quote</Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.12}
            lines={["See it dig.", { text: "On your machine.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-8xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Tell us about your fleet and we'll bring the right system to your site — installed on your excavator,
            running your soil.
          </p>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-8 lg:px-16 lg:py-24" data-testid="contact-section">
        <div className="mx-auto grid max-w-[1600px] gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <DemoForm />
            </FadeUp>
          </div>
          <div className="lg:col-span-5">
            <div className="space-y-6">
              {CONTACTS.map((c, i) => (
                <FadeUp key={c.label} delay={i * 0.06}>
                  <div className="flex items-start gap-5 border border-line bg-surface p-6">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-signal/40 text-neon">
                      <c.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fog">
                        {c.label} {c.note && <span className="text-alert">· {c.note}</span>}
                      </p>
                      <p className="mt-1.5 text-base text-white" data-testid={`contact-${c.label.toLowerCase()}`}>
                        {c.value}
                      </p>
                    </div>
                  </div>
                </FadeUp>
              ))}
              <FadeUp delay={0.24}>
                <div className="border border-signal/40 bg-signal/5 p-6">
                  <p className="font-display text-2xl font-bold uppercase text-white">Prefer to talk?</p>
                  <p className="mt-2 text-sm leading-relaxed text-fog">
                    Call us and describe your typical work — trenching, slopes, foundations — and we'll match the right
                    system before we ever set foot on site.
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-deep px-4 py-16 sm:px-8 lg:px-16 lg:py-24" data-testid="faq-section">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-5xl">
              Contractor <span className="italic text-neon">FAQ</span>
            </h2>
          </FadeUp>
          <div className="mt-10 border-t border-line">
            {FAQS.map((f, i) => (
              <FadeUp key={f.q} delay={i * 0.05} y={16}>
                <FaqItem q={f.q} a={f.a} idx={i} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
